import { ProjectData } from "types/project.types"

// Discriminated row used by the projects tree-table. Folder rows nest project
// rows via `children` (Ant Design's default tree-data field). Project rows keep
// the same `ProjectData` shape so the existing column renderers don't change.
export type ProjectTreeRow = ProjectFolderRow | ProjectRow

export interface ProjectRow extends ProjectData {
  kind: "project"
  // Ant tree-data ignores rows whose `children` is `undefined`, but adding the
  // optional field on the union saves a cast in the table component.
  children?: never
}

export interface ProjectFolderRow {
  kind: "folder"
  id: string // synthetic, e.g. "__folder__/Trackers" — must be unique for rowKey
  folderPath: string // full path: "Clients/Acme"
  folderName: string // last segment: "Acme"
  count: number // total descendant projects (recursive)
  ownerOfFolderProjects: string | null // owner of the projects inside, when uniform; null when mixed/empty
  children: ProjectTreeRow[]
}

interface FolderNode {
  folderPath: string
  folderName: string
  childFolders: Map<string, FolderNode> // keyed by lowercased segment
  projects: ProjectData[]
}

const FOLDER_PREFIX = "__folder__/"

const makeFolderNode = (
  folderPath: string,
  folderName: string
): FolderNode => ({
  folderPath,
  folderName,
  childFolders: new Map(),
  projects: [],
})

const ensureFolder = (root: FolderNode, path: string): FolderNode => {
  const segments = path.split(".").filter((s) => s.length > 0)
  let node = root
  let accum = ""
  for (const segment of segments) {
    accum = accum ? `${accum}.${segment}` : segment
    const key = segment.toLowerCase()
    let child = node.childFolders.get(key)
    if (!child) {
      child = makeFolderNode(accum, segment)
      node.childFolders.set(key, child)
    }
    node = child
  }
  return node
}

const sortByCreatedDesc = (a: ProjectData, b: ProjectData) =>
  new Date(b.metadata?.Created_at ?? 0).getTime() -
  new Date(a.metadata?.Created_at ?? 0).getTime()

const folderToRow = (node: FolderNode): ProjectFolderRow => {
  const childFolderRows = Array.from(node.childFolders.values())
    .sort((a, b) =>
      a.folderName.localeCompare(b.folderName, undefined, {
        sensitivity: "base",
      })
    )
    .map(folderToRow)

  const projectRows: ProjectRow[] = node.projects
    .slice()
    .sort(sortByCreatedDesc)
    .map((p) => ({ ...p, kind: "project" }))

  const count =
    projectRows.length +
    childFolderRows.reduce((sum, r) => sum + r.count, 0)

  // Determine if every project in the subtree shares the same owner — needed
  // for member/all views where folder-level rename/delete should only be
  // available to the owner.
  let ownerOfFolderProjects: string | null = null
  let firstOwner: string | null = null
  let mixed = false
  const collectOwners = (rows: ProjectTreeRow[]) => {
    for (const row of rows) {
      if (row.kind === "project") {
        if (firstOwner === null) firstOwner = row.owner
        else if (firstOwner !== row.owner) mixed = true
      } else {
        collectOwners(row.children)
      }
    }
  }
  collectOwners([...childFolderRows, ...projectRows])
  ownerOfFolderProjects = mixed ? null : firstOwner

  return {
    kind: "folder",
    id: `${FOLDER_PREFIX}${node.folderPath}`,
    folderPath: node.folderPath,
    folderName: node.folderName,
    count,
    ownerOfFolderProjects,
    children: [...childFolderRows, ...projectRows],
  }
}

/**
 * Builds a tree-table dataSource from a flat list of projects and a list of
 * folders the caller has explicitly created. Folder paths use `.` as the
 * segment separator (e.g. "Clients.Acme.Q4"), matching the rest of the
 * DataQ codebase. Folders that exist in `folders` but contain no projects
 * are still rendered (empty-folder support).
 *
 * Top-level rows: folder rows (alphabetical), then ungrouped project rows
 * (sorted by Created_at descending).
 *
 * Search semantics live in the caller — this function does not know about it.
 */
export const buildProjectTree = (
  projects: ProjectData[],
  folders: string[]
): ProjectTreeRow[] => {
  const root = makeFolderNode("", "")

  // Pre-create every explicit folder so empty ones render.
  for (const folderPath of folders) {
    if (folderPath) ensureFolder(root, folderPath)
  }

  const ungrouped: ProjectData[] = []
  for (const project of projects) {
    const folder = project.metadata?.Folder?.trim() ?? ""
    if (!folder) {
      ungrouped.push(project)
      continue
    }
    const node = ensureFolder(root, folder)
    node.projects.push(project)
  }

  const folderRows = Array.from(root.childFolders.values())
    .sort((a, b) =>
      a.folderName.localeCompare(b.folderName, undefined, {
        sensitivity: "base",
      })
    )
    .map(folderToRow)

  const ungroupedRows: ProjectRow[] = ungrouped
    .slice()
    .sort(sortByCreatedDesc)
    .map((p) => ({ ...p, kind: "project" }))

  return [...folderRows, ...ungroupedRows]
}

/** True if `id` looks like a synthetic folder-row id. */
export const isFolderRowId = (id: string): boolean =>
  id.startsWith(FOLDER_PREFIX)
